/// <amd-module name="TFS/Admin/Views/OrganizationOverview/Components/SoftDeletedProjects/SoftDeletedProjects" />
import * as React from "react";

import { RestoreProjectDialog } from "Components/SoftDeletedProjects/RestoreDialog";
import { RestoreTable } from "Components/SoftDeletedProjects/RestoreTable";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IOperationsRestClient } from "TFS/Admin/Common/RestClient/Operations";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import AdminResources = require("TFS/Admin/Views/OrganizationOverview/Resources");
import { ProjectState, TeamProject, TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { wait } from "VSS/Core/Util/Promise";
import { IVssPageContext } from "VSS/Platform/Context";
import { OperationStatus } from "VSS/Platform/Contracts/Operations";
import { PagedList } from "VSS/Platform/Contracts/WebApi";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ISoftDeletedProjectsProps {
    pageContext: IVssPageContext;
}

interface ISoftDeletedProjectsState {
    showRestoreDialog: boolean;
    selectedProject?: TeamProjectReference;
    softDeletedProjects?: TeamProjectReference[];
    enableRestore: boolean;
    error?: string;
    updateInProgress?: boolean;
    newProjectName?: string;
    hasMoreDeletedProjects: boolean;
}

interface IPartialTeamProject {
    state?: ProjectState;
    name?: string;
}

const progressPollInterval = 500;
const updateCheckCount = 1000;

export class SoftDeletedProjects extends React.Component<ISoftDeletedProjectsProps, ISoftDeletedProjectsState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.props.pageContext);
    private _operationId: string | null;
    private countToQuery: number = 10;

    constructor(props: ISoftDeletedProjectsProps) {
        super(props);
        this.state = {
            showRestoreDialog: false,
            enableRestore: false,
            hasMoreDeletedProjects: false
        };
        this._getDeletedProjects();
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.state.softDeletedProjects ? (
                    <div>
                        {this.state.error ? (
                            <MessageBar severity={MessageBarSeverity.Error} onDismiss={() => this.setState({ error: undefined })}>
                                {this.state.error}
                            </MessageBar>
                        ) : null}
                        <div className="soft-deleted-section flex-column">
                            <div className="title-m">{AdminResources.SoftDeletedTitle}</div>
                            <div className="note top-margin soft-deleted-text">
                                {AdminResources.SoftDeletedText}
                                <Link
                                    className="learn-more-link"
                                    href={AdminResources.SoftDeletedLearnMoreLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {AdminResources.SoftDeletedLearnMoreText}
                                </Link>
                            </div>
                            <div>
                                <RestoreTable
                                    softDeletedProjects={this.state.softDeletedProjects}
                                    OnSelectedProject={this._onSelectedProject}
                                    selectedProject={this.state.selectedProject}
                                />
                            </div>
                            {this.state.hasMoreDeletedProjects ? (
                                <div>
                                    <Link className="top-margin see-more-link" onClick={this._seeMore}>
                                        {AdminResources.SeeMoreLink}
                                    </Link>
                                </div>
                            ) : null}
                            <div>
                                <Button
                                    className="top-margin"
                                    onClick={this._onRestoreClick}
                                    disabled={!this.state.enableRestore || this.state.updateInProgress}
                                >
                                    {AdminResources.SoftDeletedRestoreButton}
                                </Button>
                            </div>
                            <div className="overview-divider" />
                        </div>

                        {this.state.showRestoreDialog ? (
                            <RestoreProjectDialog
                                pageContext={this.props.pageContext}
                                projectId={this.state.selectedProject ? this.state.selectedProject.id : ""}
                                projectName={this.state.selectedProject ? this.state.selectedProject.name : ""}
                                onCancel={this._onCancel}
                                onRestoreProject={this._onProjectRestore}
                                secondProjectName={this.state.newProjectName}
                            />
                        ) : null}

                        {this.state.updateInProgress ? <Spinner size={SpinnerSize.large} /> : null}
                    </div>
                ) : null}
            </div>
        );
    }

    private _getDeletedProjects = () => {
        const restClient = this.props.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        const actionTelemetryLogger = this._startActionCapture("Fetch deleted projects", {});
        restClient
            .getProjects(ProjectState.Deleted, this.countToQuery + 1, undefined, undefined, true)
            .then(projects => {
                if (projects && projects.length > 0) {
                    let overflow = false;
                    if (projects.length > this.countToQuery) {
                        projects = projects.splice(0, projects.length - 1) as PagedList<TeamProjectReference>;
                        overflow = true;
                    }
                    this.setState({ softDeletedProjects: projects, hasMoreDeletedProjects: overflow });
                } else {
                    this.setState({ softDeletedProjects: undefined });
                }
            })
            .catch(reason => {
                actionTelemetryLogger.captureError(reason.errorMessage);
            });
    };

    private _seeMore = () => {
        this.countToQuery += 10;
        this._getDeletedProjects();
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationOverview, Feature.OrganizationOverview, action, properties);
    };

    private _onRestoreClick = () => {
        this._startActionCapture("Restore Project Clicked", {});
        this.setState({ updateInProgress: true });
        const projectId = this.state.selectedProject ? this.state.selectedProject.id : "";
        const partialProject: IPartialTeamProject = { state: ProjectState.WellFormed };
        const projectUpdate: Partial<TeamProject> = partialProject;
        this._updateProject(projectUpdate, projectId);
    };

    private _onSelectedProject = (project: TeamProjectReference) => {
        this.setState({ selectedProject: project, enableRestore: true });
    };

    private _onCancel = () => {
        this.setState({ showRestoreDialog: false, newProjectName: undefined });
    };

    private _onProjectRestore = (projectId: string, projectName: string) => {
        this._startActionCapture("Restore Project from Dialog", {});
        this.setState({ showRestoreDialog: false, updateInProgress: true, newProjectName: projectName });
        const partialProject: IPartialTeamProject = { state: ProjectState.WellFormed, name: projectName };
        const projectUpdate: Partial<TeamProject> = partialProject;
        this._updateProject(projectUpdate, projectId);
    };

    private _updateProject(projectUpdate: Partial<TeamProject>, projectId: string) {
        const restClient = this.props.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        const restoreProjectTelemetryHelper = this._startActionCapture("Restore Project", {});
        restClient
            .updateProject(projectUpdate as TeamProject, projectId)
            .then(async result => {
                this._operationId = result.id;
                await this._monitorProgress(0);
                this._getDeletedProjects();
                this.setState({ updateInProgress: false, newProjectName: undefined, selectedProject: undefined, enableRestore: false });
            })
            .catch(reason => {
                if (reason.message.indexOf("TF200019") >= 0) {
                    const dialogTelemetryHelper = this._startActionCapture("Show Restore Project Dialog", {});
                    // These shouldn't be "" if we get here but this is one last check
                    if (this.state.selectedProject && this.state.selectedProject.id !== "" && this.state.selectedProject.name !== "") {
                        this.setState({ showRestoreDialog: true, updateInProgress: false });
                    } else {
                        this.setState({ updateInProgress: false, error: AdminResources.RestoreProjectError, newProjectName: undefined });
                        dialogTelemetryHelper.captureError("Restore Project Failure with matching name error but no name state");
                    }
                } else {
                    this.setState({ updateInProgress: false, error: reason.message, newProjectName: undefined });
                    restoreProjectTelemetryHelper.captureError(reason.message);
                }
            });
    }

    /**
     * Poll operations client to see if operation has completed.
     */
    private _monitorProgress = async (updateCount: number): Promise<void> => {
        if (updateCheckCount > updateCount) {
            if (this._operationId) {
                const operationsClient = this.props.pageContext.getRestClient<IOperationsRestClient>("IOperationsRestClient");
                let completed = false;

                try {
                    const op = await operationsClient.getOperation(this._operationId);

                    switch (op.status) {
                        case OperationStatus.Succeeded:
                            completed = true;
                            break;
                        case OperationStatus.Cancelled:
                        case OperationStatus.Failed:
                            completed = true;
                            this.setState({ error: op.resultMessage });
                            break;
                    }
                } catch {
                    this.setState({
                        error: AdminResources.RestoreProjectError,
                        updateInProgress: false
                    });
                }

                if (completed) {
                    this._operationId = null;
                } else {
                    await wait(progressPollInterval);
                    await this._monitorProgress(updateCount + 1);
                }
            }
        }
    };
}
